import type { OrgRole, PlatformRole, PortalRole } from './types';

/**
 * Role-based navigation and guards (specs/02 + specs/04).
 * The UI only hides what is not allowed; RLS is the real enforcement.
 */

export type ClubRouteKey =
  | 'dashboard'
  | 'doors'
  | 'persons'
  | 'groups'
  | 'codes'
  | 'loans'
  | 'logbook'
  | 'import'
  | 'settings'
  | 'help';

export type PlatformRouteKey = 'clubs' | 'inventory' | 'installers' | 'team' | 'alerts' | 'faq' | 'audit';

export interface NavItem<K extends string> {
  key: K;
  label: string;
  path: string;
  roles: readonly PortalRole[];
}

export const CLUB_NAV: readonly NavItem<ClubRouteKey>[] = [
  { key: 'dashboard', label: 'Dashboard', path: 'dashboard', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  // installer: alleen bij een geldige toewijzing aan deze club (locaties, deuren, sloten koppelen).
  { key: 'doors', label: 'Deuren', path: 'deuren', roles: ['platform_admin', 'support', 'club_admin', 'installer'] },
  { key: 'persons', label: 'Personen', path: 'personen', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  { key: 'groups', label: 'Groepen', path: 'groepen', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  { key: 'codes', label: 'Codes', path: 'codes', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  { key: 'loans', label: 'Uitleenpassen', path: 'uitleenpassen', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  // spec 02 note 8: group_manager sees events of own group members on own group doors.
  { key: 'logbook', label: 'Logboek', path: 'logboek', roles: ['platform_admin', 'support', 'club_admin', 'group_manager'] },
  { key: 'import', label: 'Import', path: 'import', roles: ['platform_admin', 'club_admin'] },
  { key: 'settings', label: 'Instellingen', path: 'instellingen', roles: ['platform_admin', 'support', 'club_admin'] },
  { key: 'help', label: 'Hulp', path: 'hulp', roles: ['platform_admin', 'support', 'club_admin', 'group_manager', 'installer'] },
] as const;

export const PLATFORM_NAV: readonly NavItem<PlatformRouteKey>[] = [
  { key: 'clubs', label: 'Clubs', path: 'clubs', roles: ['platform_admin', 'support'] },
  { key: 'inventory', label: 'Voorraad sloten', path: 'voorraad', roles: ['platform_admin', 'support'] },
  { key: 'installers', label: 'Installateurs', path: 'installateurs', roles: ['platform_admin'] },
  { key: 'team', label: 'Team', path: 'team', roles: ['platform_admin', 'support'] },
  { key: 'alerts', label: 'Meldingen', path: 'meldingen', roles: ['platform_admin', 'support'] },
  { key: 'faq', label: 'Hulpteksten', path: 'hulpteksten', roles: ['platform_admin', 'support'] },
  { key: 'audit', label: 'Audit', path: 'audit', roles: ['platform_admin', 'support'] },
] as const;

export interface RoleContext {
  platformRole: PlatformRole | null;
  /** Role in the currently selected club (null when none selected / no membership). */
  orgRole: OrgRole | null;
  /** Installer with a valid installer_assignment for the selected club. */
  installerAssigned?: boolean;
}

/**
 * The effective portal role for the selected club. Platform staff look at a club through
 * impersonation; `member` does not use the club portal (app only). An `installer` only sees the doors of clubs he
 * is assigned to (installer_assignments), to create locations/doors and link locks.
 */
export function effectiveClubRole(ctx: RoleContext): PortalRole | null {
  if (ctx.platformRole === 'platform_admin') return 'platform_admin';
  if (ctx.platformRole === 'support') return 'support';
  if (ctx.orgRole === 'club_admin' || ctx.orgRole === 'group_manager') return ctx.orgRole;
  // Installateur: Antreja-team of door de club aangewezen (installer_assignments.kind = 'club', migratie 0020).
  if (ctx.installerAssigned) return 'installer';
  return null;
}

/**
 * Alle rollen die iemand in deze club heeft (rollen stapelen). Voorbeeld: een installateur die bij dezelfde club ook
 * groepsbeheerder is, krijgt de menu's van beide. De eerste is de hoofdrol (effectiveClubRole).
 */
export function effectiveClubRoles(ctx: RoleContext): PortalRole[] {
  const primary = effectiveClubRole(ctx);
  if (!primary) return [];
  const out: PortalRole[] = [primary];
  if ((ctx.orgRole === 'club_admin' || ctx.orgRole === 'group_manager') && !out.includes(ctx.orgRole)) out.push(ctx.orgRole);
  if (ctx.installerAssigned && !out.includes('installer')) out.push('installer');
  return out;
}

export function effectivePlatformRole(ctx: RoleContext): PortalRole | null {
  if (ctx.platformRole === 'platform_admin' || ctx.platformRole === 'support') return ctx.platformRole;
  return null;
}

export function clubNavFor(ctx: RoleContext): NavItem<ClubRouteKey>[] {
  const roles = effectiveClubRoles(ctx);
  return CLUB_NAV.filter((n) => roles.some((r) => n.roles.includes(r)));
}

export function platformNavFor(ctx: RoleContext): NavItem<PlatformRouteKey>[] {
  const role = effectivePlatformRole(ctx);
  if (!role) return [];
  return PLATFORM_NAV.filter((n) => n.roles.includes(role));
}

export function canAccessClubRoute(key: ClubRouteKey, ctx: RoleContext): boolean {
  return clubNavFor(ctx).some((n) => n.key === key);
}

export function canAccessPlatformRoute(key: PlatformRouteKey, ctx: RoleContext): boolean {
  return platformNavFor(ctx).some((n) => n.key === key);
}

/** Fine-grained actions inside screens (spec 02 matrix). */
export type Action =
  | 'person.create'
  | 'person.archive'
  | 'person.invite'
  | 'person.app_admin'
  | 'installer.club_assign'
  | 'loan_card.create'
  | 'loan_card.lend'
  | 'group.create'
  | 'group.edit_rules'
  | 'group.duplicate'
  | 'group_member.edit'
  | 'credential.revoke'
  | 'code.create'
  | 'code.revoke'
  | 'import.run'
  | 'settings.edit'
  | 'door.passage_mode'
  | 'door.auto_lock'
  | 'lock.battery_replaced'
  | 'door.manage'
  | 'door.archive'
  | 'door.safety'
  | 'lock.unlink_installed'
  | 'admins.manage'
  | 'platform.manage';

const ACTIONS: Record<Action, readonly PortalRole[]> = {
  'person.create': ['platform_admin', 'club_admin', 'group_manager'],
  'person.archive': ['platform_admin', 'club_admin'],
  /** Uitnodigen voor de app; group_manager alleen voor leden van de eigen groep (server-side afgedwongen). */
  'person.invite': ['platform_admin', 'club_admin', 'group_manager'],
  /** Uitnodiging intrekken / app ontkoppelen. */
  'person.app_admin': ['platform_admin', 'club_admin'],
  'installer.club_assign': ['platform_admin', 'club_admin'],
  'loan_card.create': ['platform_admin', 'club_admin'],
  /** Uitlenen/innemen; group_manager alleen passen in de eigen groep (server-side afgedwongen). */
  'loan_card.lend': ['platform_admin', 'club_admin', 'group_manager'],
  'group.create': ['platform_admin', 'club_admin'],
  'group.edit_rules': ['platform_admin', 'club_admin'],
  'group.duplicate': ['platform_admin', 'club_admin'],
  'group_member.edit': ['platform_admin', 'club_admin', 'group_manager'],
  'credential.revoke': ['platform_admin', 'club_admin'],
  'code.create': ['platform_admin', 'club_admin', 'group_manager'],
  'code.revoke': ['platform_admin', 'club_admin', 'group_manager'],
  'import.run': ['platform_admin', 'club_admin'],
  'settings.edit': ['platform_admin', 'club_admin'],
  'door.passage_mode': ['platform_admin', 'club_admin'],
  /** Automatisch op slot per deur (set_door_auto_lock). */
  'door.auto_lock': ['platform_admin', 'club_admin'],
  'lock.battery_replaced': ['platform_admin', 'club_admin'],
  /** Locaties en deuren aanmaken/wijzigen, slot koppelen (RPC's save_location, save_door, link_lock_to_door). */
  'door.manage': ['platform_admin', 'club_admin', 'installer'],
  /** Deur archiveren, locatie verwijderen. */
  'door.archive': ['platform_admin', 'club_admin'],
  /** Nooduitgang en passagemodus-toestemming. */
  'door.safety': ['platform_admin', 'club_admin'],
  /** Een geïnstalleerd slot van de deur halen. */
  'lock.unlink_installed': ['platform_admin'],
  /** Club- en groepsbeheerders uitnodigen, rol wijzigen, rechten intrekken. */
  'admins.manage': ['platform_admin', 'club_admin'],
  'platform.manage': ['platform_admin'],
};

export function can(action: Action, role: PortalRole | null): boolean {
  return role !== null && ACTIONS[action].includes(role);
}

/** Zoals can(), maar met alle rollen in deze club (rollen stapelen). */
export function canAny(action: Action, roles: readonly PortalRole[]): boolean {
  return roles.some((r) => ACTIONS[action].includes(r));
}

/**
 * TOTP (aal2) is mandatory for platform_admin and club_admin (spec 04, bevestigd 30 okt 2026), and for every other
 * member of the Antreja team in the portal (support needs aal2 for start_impersonation; installers see several clubs).
 */
export function requiresMfa(platformRole: PlatformRole | null, orgRoles: readonly OrgRole[], installer = false): boolean {
  // Installateurs (ook clubinstallateurs) werken op de server alleen met aal2 (security review H6).
  return platformRole !== null || orgRoles.includes('club_admin') || installer;
}

export const ROLE_LABEL: Record<PortalRole, string> = {
  platform_admin: 'Platformbeheerder',
  support: 'Support (alleen lezen)',
  club_admin: 'Clubbeheerder',
  group_manager: 'Groepsbeheerder',
  installer: 'Installateur',
};

/** Rollen van het Antreja-team (platform_staff). */
export const STAFF_ROLE_LABEL: Record<PlatformRole, string> = {
  platform_admin: 'Platformbeheerder',
  installer: 'Installateur',
  support: 'Support (alleen lezen)',
};

export const STAFF_ROLE_HINT: Record<PlatformRole, string> = {
  platform_admin: 'Alles: clubs, sloten, team en meekijken bij clubs.',
  installer: 'Werkt bij clubs waaraan hij is toegewezen: deuren aanmaken, sloten koppelen en installeren.',
  support: 'Kan alles bekijken en meekijken bij clubs, maar niets wijzigen.',
};
